import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React, { useEffect, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
var PRODUCT_IMAGES_URLS = [
    "https://cdn.faire.com/fastly/82a7201c490fe38a262f0a67bb2454b81b0c60231c200ebb96689bc954d0fb87.png",
    "https://cdn.faire.com/fastly/f813d877520aaaf3c69dfce3552e53c4d69dab04a32c9d047e1d2847230a2d83.png",
    "https://cdn.faire.com/fastly/933bdfc099d7f30f91dca08c245eaa7d65db9bda7bd663cafeb79fb0a39ea3c9.png"
];
var ANIMATION_INTERVAL_MS = 2500;
var TRANSITION_DURATION_MS = 500;
export var MultiVariantIllustration = function(param) {
    var visible = param.visible;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var isSSR = useIsSSR();
    var _React_useState = _sliced_to_array(React.useState(true), 2), isGrouped = _React_useState[0], setIsGrouped = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(false), 2), delay = _React_useState1[0], setDelay = _React_useState1[1];
    // Store delay in a ref to avoid using as dependency in useEffect
    var delayRef = React.useRef(delay);
    delayRef.current = delay;
    var delayTimeoutId = useRef(undefined);
    var optimizedImageUrls = PRODUCT_IMAGES_URLS.map(function(url) {
        var _optimizeImageUrl;
        return (_optimizeImageUrl = optimizeImageUrl({
            url: url
        }, isSSR)) !== null && _optimizeImageUrl !== void 0 ? _optimizeImageUrl : "";
    });
    var variants = getVariants(strictLocalize, optimizedImageUrls);
    useEffect(function() {
        if (!visible) {
            // Reset the transition state when component is hidden
            if (delayRef.current) {
                setDelay(false);
            }
            return;
        }
        var intervalId = setInterval(function() {
            if (!delayRef.current) {
                setIsGrouped(function(prevState) {
                    return !prevState;
                });
                // Animation interval paused to give time for the transition to complete
                setDelay(true);
                clearTimeout(delayTimeoutId.current);
                delayTimeoutId.current = setTimeout(function() {
                    return setDelay(false);
                }, TRANSITION_DURATION_MS);
            }
        }, ANIMATION_INTERVAL_MS);
        return function() {
            clearInterval(intervalId);
            clearTimeout(delayTimeoutId.current);
        };
    }, [
        visible
    ]);
    // Keep the variant components mounted and just update their text
    // to avoid refetching images on every animation interval.
    return /*#__PURE__*/ _jsx(Container, {
        align: "center",
        justify: "center",
        children: /*#__PURE__*/ _jsxs(ProductContainer, {
            justify: "center",
            children: [
                /*#__PURE__*/ _jsxs(ProductHeader, {
                    $isGrouped: isGrouped,
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            align: "center",
                            gap: "2x",
                            children: [
                                /*#__PURE__*/ _jsx(VariantImage, {
                                    src: optimizedImageUrls[0]
                                }),
                                /*#__PURE__*/ _jsxs(ItemNameWrapper, {
                                    $showTransition: delay,
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: {
                                                mobileAndAbove: "paragraphSansMedium",
                                                desktopAndAbove: "subheadingSansMedium"
                                            },
                                            color: Core.text.subdued,
                                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                id: "DnDv7q",
                                                defaultMessage: [
                                                    {
                                                        type: 0,
                                                        value: "Apron"
                                                    }
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: {
                                                mobileAndAbove: "paragraphSansRegular",
                                                desktopAndAbove: "subheadingSansRegular"
                                            },
                                            color: Core.text.subdued,
                                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                                id: "ocaW4G",
                                                defaultMessage: [
                                                    {
                                                        type: 0,
                                                        value: "3 variants"
                                                    }
                                                ]
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: isTabletAndBelow ? "3x" : "4x"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(VariantsContainer, {
                    $isGrouped: isGrouped,
                    gap: {
                        mobileAndAbove: "3x",
                        desktopAndAbove: "4x"
                    },
                    children: variants.map(function(param, index) {
                        var groupedName = param.groupedName, splitName = param.splitName, imageUrl = param.imageUrl;
                        return /*#__PURE__*/ _jsx(Variant, {
                            imageUrl: imageUrl,
                            name: isGrouped ? groupedName : splitName,
                            showTransition: delay
                        }, index);
                    })
                })
            ]
        })
    });
};
var Variant = function(param) {
    var imageUrl = param.imageUrl, name = param.name, showTransition = param.showTransition;
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsxs(Row, {
        align: "center",
        gap: "2x",
        children: [
            /*#__PURE__*/ _jsx(VariantImage, {
                src: optimizeImageUrl({
                    url: imageUrl
                }, isSSR)
            }),
            /*#__PURE__*/ _jsx(ItemNameWrapper, {
                $showTransition: showTransition,
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: {
                        mobileAndAbove: "paragraphSansMedium",
                        desktopAndAbove: "subheadingSansMedium"
                    },
                    color: Core.text.subdued,
                    children: name
                })
            })
        ]
    });
};
var getVariants = function(strictLocalize, optimizedImageUrls) {
    return [
        {
            groupedName: strictLocalize({
                id: "X+lXR8",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Mustard"
                    }
                ]
            }),
            splitName: strictLocalize({
                id: "TEV6dB",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Apron: Mustard"
                    }
                ]
            }),
            imageUrl: optimizedImageUrls[0]
        },
        {
            groupedName: strictLocalize({
                id: "GAacva",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Olive"
                    }
                ]
            }),
            splitName: strictLocalize({
                id: "/PR60q",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Apron: Olive"
                    }
                ]
            }),
            imageUrl: optimizedImageUrls[1]
        },
        {
            groupedName: strictLocalize({
                id: "LySIwL",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Terracotta"
                    }
                ]
            }),
            splitName: strictLocalize({
                id: "wp2SQe",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Apron: Terracotta"
                    }
                ]
            }),
            imageUrl: optimizedImageUrls[2]
        }
    ];
};
var Container = styled(Column).withConfig({
    componentId: "sc-fe030f20-0"
})([
    "height:584px;width:100%;max-width:584px;background-color:",
    ";user-select:none;",
    "{height:343px;}"
], Decorative.icon.green, responsiveMediaQueries.tabletAndBelow);
var ProductContainer = styled(Column).withConfig({
    componentId: "sc-fe030f20-1"
})([
    "height:399px;width:399px;padding:",
    ";border-radius:4px;background-color:",
    ";box-shadow:0px 4px 10px 0px rgba(0,0,0,0.1);padding:",
    ";",
    "{height:295px;width:280px;padding:",
    ";}"
], getSpacing("4x"), Core.surface.inverse, getSpacing("4x"), responsiveMediaQueries.tabletAndBelow, getSpacing("3x"));
var ProductHeader = styled(Column).withConfig({
    componentId: "sc-fe030f20-2"
})([
    "max-height:",
    ";opacity:",
    ";transition:max-height ",
    "ms ",
    "ms ease-in-out,opacity ",
    "ms ease-in-out;"
], function(props) {
    return props.$isGrouped ? "100%" : "0";
}, function(props) {
    return props.$isGrouped ? "1" : "0";
}, TRANSITION_DURATION_MS / 2, TRANSITION_DURATION_MS / 2, TRANSITION_DURATION_MS / 2);
var VariantsContainer = styled(Column).attrs({
    gap: {
        mobileAndAbove: "3x",
        desktopAndAbove: "4x"
    }
}).withConfig({
    componentId: "sc-fe030f20-3"
})([
    "padding-left:",
    ";",
    "{padding-left:",
    ";}transition:padding-left ",
    "ms ",
    "ms ease-in-out;"
], function(props) {
    return props.$isGrouped ? getSpacing("9x") : 0;
}, responsiveMediaQueries.tabletAndBelow, function(props) {
    return props.$isGrouped ? getSpacing("7x") : 0;
}, TRANSITION_DURATION_MS / 2, TRANSITION_DURATION_MS / 2);
var VariantImage = styled.img.withConfig({
    componentId: "sc-fe030f20-4"
})([
    "width:",
    ";height:",
    ";",
    "{width:",
    ";height:",
    ";}"
], getSpacing("7x"), getSpacing("7x"), responsiveMediaQueries.tabletAndBelow, getSpacing("5x"), getSpacing("5x"));
var opacityAnimation = keyframes([
    "100%{opacity:1;}0%,50%{opacity:0;}"
]);
var ItemNameWrapper = styled(Column).withConfig({
    componentId: "sc-fe030f20-5"
})([
    "animation:",
    ";"
], function(props) {
    return props.$showTransition ? css([
        "",
        " ",
        "ms ease-in-out"
    ], opacityAnimation, TRANSITION_DURATION_MS) : "none";
});
